import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react"
import { SceneControlIcon } from "@/components/locations/scene-control-icon"
import type { SceneFlyAxis } from "@/lib/location-scene-3d/engine"
import type { SceneThemeId } from "@/lib/location-scene-3d/scene-state"
import { cn } from "@/lib/utils"
import "@/components/locations/scene-controls.css"

export const MOVE_KEY_CODES = ["KeyW", "KeyA", "KeyS", "KeyD", "KeyR", "KeyF"] as const

export type MoveKeyCode = (typeof MOVE_KEY_CODES)[number]

export const MOVE_KEY_AXIS: Record<MoveKeyCode, SceneFlyAxis> = {
  KeyW: "f",
  KeyA: "l",
  KeyS: "b",
  KeyD: "r",
  KeyR: "u",
  KeyF: "d",
}

const MOVE_KEY_SET = new Set<string>(MOVE_KEY_CODES)

type MoveKeyHints = Record<MoveKeyCode, string>

type MoveKeysProps = {
  skin: SceneThemeId
  hints: MoveKeyHints
  onPress: (code: MoveKeyCode) => void
  onRelease: (code: MoveKeyCode) => void
}

function isMoveKeyCode(code: string): code is MoveKeyCode {
  return MOVE_KEY_SET.has(code)
}

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === "TEXTAREA" || tag === "SELECT") return true
  if (tag === "INPUT") {
    const type = (target as HTMLInputElement).type
    return type !== "range" && type !== "checkbox" && type !== "radio" && type !== "button" && type !== "submit"
  }
  return false
}

function stopSceneEvent(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}

function KeyFace({
  code,
  letter,
  hint,
  pressed,
  arrow,
  onPress,
  onRelease,
}: {
  code: MoveKeyCode
  letter: string
  hint: string
  pressed: boolean
  arrow?: "up" | "down"
  onPress: (code: MoveKeyCode, event: ReactPointerEvent<HTMLDivElement>) => void
  onRelease: (code: MoveKeyCode) => void
}) {
  return (
    <div
      role="img"
      aria-label={hint}
      title={hint}
      data-pressed={pressed ? "true" : "false"}
      className={cn(
        "box-border flex size-[34px] cursor-pointer flex-col items-center justify-center",
        "rounded-lg border border-solid",
        "border-[var(--sc-border)] bg-[var(--sc-surface)] text-[13px] leading-none text-[color:var(--sc-fg)]",
        "transition-[background-color,border-color,color] duration-[80ms]",
        pressed && "border-[var(--sc-pressed-border)] bg-[var(--sc-pressed-bg)] text-[color:var(--sc-pressed-fg)]",
      )}
      onPointerDown={(event) => onPress(code, event)}
      onPointerUp={() => onRelease(code)}
      onPointerCancel={() => onRelease(code)}
      onLostPointerCapture={() => onRelease(code)}
    >
      <span>{letter}</span>
      {arrow ? (
        <span className={cn("text-[color:var(--sc-fg-muted)]", pressed && "text-[color:var(--sc-pressed-fg)]")}>
          <SceneControlIcon name={arrow} size={11} />
        </span>
      ) : null}
    </div>
  )
}

export function MoveKeys({ skin, hints, onPress, onRelease }: MoveKeysProps) {
  const [keyboardDown, setKeyboardDown] = useState<ReadonlySet<MoveKeyCode>>(() => new Set())
  const [pointerDown, setPointerDown] = useState<MoveKeyCode | null>(null)
  const pointerDownRef = useRef<MoveKeyCode | null>(null)
  const onPressRef = useRef(onPress)
  const onReleaseRef = useRef(onRelease)
  onPressRef.current = onPress
  onReleaseRef.current = onRelease

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (!isMoveKeyCode(event.code) || event.repeat) return
      if (event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) return
      const code = event.code
      setKeyboardDown((prev) => {
        if (prev.has(code)) return prev
        const next = new Set(prev)
        next.add(code)
        return next
      })
    }
    const up = (event: KeyboardEvent) => {
      if (!isMoveKeyCode(event.code)) return
      const code = event.code
      setKeyboardDown((prev) => {
        if (!prev.has(code)) return prev
        const next = new Set(prev)
        next.delete(code)
        return next
      })
    }
    const clear = () => setKeyboardDown(new Set())
    const onHide = () => {
      if (document.hidden) clear()
    }
    window.addEventListener("keydown", down)
    window.addEventListener("keyup", up)
    window.addEventListener("blur", clear)
    document.addEventListener("visibilitychange", onHide)
    return () => {
      window.removeEventListener("keydown", down)
      window.removeEventListener("keyup", up)
      window.removeEventListener("blur", clear)
      document.removeEventListener("visibilitychange", onHide)
    }
  }, [])

  const press = (code: MoveKeyCode, event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    pointerDownRef.current = code
    setPointerDown(code)
    onPressRef.current(code)
  }

  const release = (code: MoveKeyCode) => {
    if (pointerDownRef.current !== code) return
    pointerDownRef.current = null
    setPointerDown(null)
    onReleaseRef.current(code)
  }

  const pressed = (code: MoveKeyCode) => keyboardDown.has(code) || pointerDown === code

  return (
    <div
      className="scene-controls inline-flex items-end gap-[14px]"
      data-skin={skin}
      onPointerDown={stopSceneEvent}
      onPointerUp={stopSceneEvent}
      onClick={stopSceneEvent}
      onDoubleClick={stopSceneEvent}
      onWheel={stopSceneEvent}
      onContextMenu={stopSceneEvent}
    >
      <div className="grid grid-cols-3 gap-[5px]">
        <span aria-hidden />
        <KeyFace
          code="KeyW"
          letter="W"
          hint={hints.KeyW}
          pressed={pressed("KeyW")}
          onPress={press}
          onRelease={release}
        />
        <span aria-hidden />
        <KeyFace
          code="KeyA"
          letter="A"
          hint={hints.KeyA}
          pressed={pressed("KeyA")}
          onPress={press}
          onRelease={release}
        />
        <KeyFace
          code="KeyS"
          letter="S"
          hint={hints.KeyS}
          pressed={pressed("KeyS")}
          onPress={press}
          onRelease={release}
        />
        <KeyFace
          code="KeyD"
          letter="D"
          hint={hints.KeyD}
          pressed={pressed("KeyD")}
          onPress={press}
          onRelease={release}
        />
      </div>
      <div className="grid gap-[5px]">
        <KeyFace
          code="KeyR"
          letter="R"
          hint={hints.KeyR}
          pressed={pressed("KeyR")}
          arrow="up"
          onPress={press}
          onRelease={release}
        />
        <KeyFace
          code="KeyF"
          letter="F"
          hint={hints.KeyF}
          pressed={pressed("KeyF")}
          arrow="down"
          onPress={press}
          onRelease={release}
        />
      </div>
    </div>
  )
}
